/**
 * Location/topology of a block within the content tree — the data the
 * context menu needs to enable/disable and target its actions. Pure
 * module (unit-testable). Index/count use the raw sibling list so the
 * enable state matches store.moveBlock() semantics exactly.
 */

import type { BlockNode } from './types';
import { markerId } from './types';

export interface BlockContext {
    /** Parent marker id, null = content root. */
    parentId: string | null;
    /** Slot the block renders in (sparse contract: absence = 0). */
    slot: number;
    /** Index within the raw sibling list. */
    index: number;
    /** Raw sibling count. */
    count: number;
    /** Own id — the insert-before target. */
    beforeId: string | null;
    /** Next raw sibling id — the insert-after target (null = append). */
    nextId: string | null;
}

/**
 * Resolves the context of a block anywhere in the tree, DFS.
 *
 * @param content Content tree roots.
 * @param id      Marker id to find.
 * @return The context, or null when the id does not resolve.
 */
export function blockContextOf(content: BlockNode[], id: string): BlockContext | null {
    const visit = (list: BlockNode[], parent: BlockNode | null): BlockContext | null => {
        for (let index = 0; index < list.length; index++) {
            const block = list[index];

            if (markerId(block) === id) {
                const next = list[index + 1];

                return {
                    parentId: parent === null ? null : markerId(parent),
                    slot: Number(block.slot ?? 0),
                    index,
                    count: list.length,
                    beforeId: markerId(block),
                    nextId: next ? markerId(next) : null,
                };
            }

            if (block.children?.length) {
                const found = visit(block.children, block);

                if (found) {
                    return found;
                }
            }
        }

        return null;
    };

    return visit(content, null);
}
